// グローバルキーボードショートカット。
// - Ctrl + 「+」「-」: 段階ズーム（画面中央が中心）
// - Ctrl + C / X / V: 選択中オブジェクトのコピー / 切り取り / 貼り付け（貼り付けは use-paste-handler）
// - Ctrl + Z / Y: 自分の操作の Undo / Redo
// - Esc: ツール解除（idle へ戻る）
// - Delete / Backspace: 選択中オブジェクトの削除
// - Ctrl + ] / [: 選択中オブジェクトの前面 / 背面移動

import { useEffect } from "react";
import { useShowToast } from "@meiden-connect/ui";
import type { CanvasObject } from "@refinement-canvas/domain";
import { useCanvasStore } from "../store/canvas-store";
import { useCanvasDocStore } from "../store/canvas-doc-store";
import { stepZoomIn, stepZoomOut, zoomAt } from "../../../lib/canvas-coordinates";
import { writeCanvasObjectsToClipboardData } from "../../../lib/canvas-clipboard";
import type { CanvasOpDispatcher } from "../use-canvas-op-dispatcher";
import { isTransientAttachmentSrc } from "../attachment-placeholder";

export function useGlobalShortcuts(dispatcher?: CanvasOpDispatcher | undefined): void {
  const setView = useCanvasStore((s) => s.setView);
  const resetMode = useCanvasStore((s) => s.resetMode);
  const showToast = useShowToast();

  useEffect(() => {
    const onCopy = (event: ClipboardEvent): void => {
      if (isEditingTarget(event.target) || !event.clipboardData) return;
      const selected = useCanvasStore.getState().selectedObjectIds;
      if (selected.length === 0) return;
      const objects = useCanvasDocStore.getState().objects;
      const copied = selected.map((id) => objects[id]).filter((o): o is NonNullable<typeof o> => Boolean(o));
      if (copied.length === 0) return;
      // アップロード未完了の画像・動画（blob:/data: src）は他参加者が解決できないため、
      // コピー対象から除外する（複製による壊れた画像や永続仮表示の再発を防ぐ）。
      // 混在選択では未完了添付だけを除外し、確定済みのオブジェクトは通常通りコピーする。
      const copyable = copied.filter((o) => !isTransientAttachmentObject(o));
      if (copyable.length === 0) {
        event.preventDefault();
        showToast("warning", "アップロード完了前の画像・動画はコピーできません。完了後にもう一度お試しください。");
        return;
      }
      writeCanvasObjectsToClipboardData(event.clipboardData, copyable);
      event.preventDefault();
      if (copyable.length < copied.length) {
        showToast("info", "アップロード完了前の画像・動画はコピー対象から除外しました。");
      }
    };

    // Ctrl+X: コピーと同じシリアライズを行ったうえで選択中オブジェクトを削除する。
    // 入力中のテキストフィールド側の「文字列の切り取り」操作を奪わないよう、編集対象のフォーカス時は何もしない。
    // ロックされているオブジェクトは削除対象から除外する（クリップボードへは含めて良い）。
    const onCut = (event: ClipboardEvent): void => {
      if (isEditingTarget(event.target) || !event.clipboardData) return;
      const selected = useCanvasStore.getState().selectedObjectIds;
      if (selected.length === 0) return;
      const objects = useCanvasDocStore.getState().objects;
      const copied = selected.map((id) => objects[id]).filter((o): o is NonNullable<typeof o> => Boolean(o));
      if (copied.length === 0) return;
      // コピーと同様に、アップロード未完了の画像・動画は切り取り（=複製＋削除）対象から除外する。
      // 未完了添付はクリップボードへ入れず、削除もせずその場に残す。
      const copyable = copied.filter((o) => !isTransientAttachmentObject(o));
      if (copyable.length === 0) {
        event.preventDefault();
        showToast("warning", "アップロード完了前の画像・動画は切り取りできません。完了後にもう一度お試しください。");
        return;
      }
      writeCanvasObjectsToClipboardData(event.clipboardData, copyable);
      event.preventDefault();
      if (dispatcher) {
        const copyableIds = new Set(copyable.map((o) => o.id));
        // 切り取り対象（=クリップボードへ入れたもの）のうち、ロックされていないものだけ削除する。
        const deletable = selected.filter((id) => copyableIds.has(id) && !objects[id]?.locked);
        if (deletable.length > 0) {
          dispatcher.deleteObjects(deletable);
        }
        useCanvasStore.getState().setSelectedObjectIds([]);
      }
      if (copyable.length < copied.length) {
        showToast("info", "アップロード完了前の画像・動画は切り取り対象から除外しました。");
      }
    };

    const onKeyDown = (event: KeyboardEvent): void => {
      const editingTarget = isEditingTarget(event.target);

      if (event.key === "Escape") {
        resetMode();
        return;
      }

      if ((event.key === "Delete" || event.key === "Backspace") && !editingTarget) {
        const selected = useCanvasStore.getState().selectedObjectIds;
        if (selected.length > 0 && dispatcher) {
          // 選択がある時点で「キャンバスへの削除ショートカット」として扱われるため、
          // 実際に削除対象が無い（全部ロック中など）場合でもブラウザ既定動作
          // （例: Backspace による履歴戻り）には流さない。
          event.preventDefault();
          // ロックされているオブジェクトは削除対象から除外する。
          const objects = useCanvasDocStore.getState().objects;
          const deletable = selected.filter((id) => !objects[id]?.locked);
          if (deletable.length > 0) {
            dispatcher.deleteObjects(deletable);
            useCanvasStore.getState().setSelectedObjectIds([]);
          }
        }
        return;
      }

      if (!(event.ctrlKey || event.metaKey) || editingTarget) return;

      const key = event.key.toLowerCase();
      if (key === "z") {
        event.preventDefault();
        dispatcher?.undo();
        return;
      }
      if (key === "y") {
        event.preventDefault();
        dispatcher?.redo();
        return;
      }

      if (event.key === "]") {
        bringForward(dispatcher);
        event.preventDefault();
        return;
      }
      if (event.key === "[") {
        sendBackward(dispatcher);
        event.preventDefault();
        return;
      }

      if (event.key === "+" || event.key === ";" || event.key === "=") {
        // ; や = は Ctrl+= で来る環境がある。いずれもズームインに割り当てる。
        event.preventDefault();
        const view = useCanvasStore.getState().view;
        const center = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
        setView(zoomAt(view, center, stepZoomIn(view.zoom)));
      } else if (event.key === "-" || event.key === "_") {
        event.preventDefault();
        const view = useCanvasStore.getState().view;
        const center = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
        setView(zoomAt(view, center, stepZoomOut(view.zoom)));
      }
    };
    window.addEventListener("copy", onCopy);
    window.addEventListener("cut", onCut);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("copy", onCopy);
      window.removeEventListener("cut", onCut);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [dispatcher, resetMode, setView, showToast]);
}

// 「アップロード未完了の画像・動画」（blob:/data: src）かどうか。
// コピー/切り取りの除外判定に使う（確定＝公開 URL 後のみ複製を許可する）。
function isTransientAttachmentObject(object: CanvasObject): boolean {
  return (
    (object.type === "image" || object.type === "video") && isTransientAttachmentSrc(object.src)
  );
}

function bringForward(dispatcher: CanvasOpDispatcher | undefined): void {
  if (!dispatcher) return;
  const selected = useCanvasStore.getState().selectedObjectIds;
  if (selected.length === 0) return;
  const objects = useCanvasDocStore.getState().objects;
  // ロック中オブジェクトは zOrder 変更の対象外。複数選択中も locked のものだけ除外する。
  const movable = selected.filter((id) => {
    const object = objects[id];
    return object !== undefined && !object.locked;
  });
  if (movable.length === 0) return;
  const maxZ = Math.max(0, ...Object.values(objects).map((o) => o.zOrder));
  // 1 ショートカット操作 = 1 履歴エントリにまとめ、Ctrl+Z 1 回でグループ全体を戻せるようにする。
  const updates = movable.map((id, index) => ({
    objectId: id,
    transform: { zOrder: maxZ + 1 + index },
  }));
  dispatcher.transformObjects(updates);
}

function sendBackward(dispatcher: CanvasOpDispatcher | undefined): void {
  if (!dispatcher) return;
  const selected = useCanvasStore.getState().selectedObjectIds;
  if (selected.length === 0) return;
  const objects = useCanvasDocStore.getState().objects;
  // ロック中オブジェクトは zOrder 変更の対象外。複数選択中も locked のものだけ除外する。
  const movable = selected.filter((id) => {
    const object = objects[id];
    return object !== undefined && !object.locked;
  });
  if (movable.length === 0) return;
  const minZ = Math.min(0, ...Object.values(objects).map((o) => o.zOrder));
  // 1 ショートカット操作 = 1 履歴エントリにまとめる（前面移動と同様）。
  const updates = movable.map((id, index) => ({
    objectId: id,
    transform: { zOrder: minZ - 1 - index },
  }));
  dispatcher.transformObjects(updates);
}

function isEditingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName.toLowerCase();
  return tag === "input" || tag === "textarea";
}
